
import { useParams } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import { worldCountries } from '@/data/countries/index';
import SEO from '@/components/shared/SEO';
import CountrySEOContent from '@/components/country/CountrySEOContent';
import CountryHeroSection from '@/components/country/CountryHeroSection';
import CountryCitiesGrid from '@/components/country/CountryCitiesGrid';
import CountryRelatedSection from '@/components/country/CountryRelatedSection';
import CountryCallToAction from '@/components/country/CountryCallToAction';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const CountryDirectory = () => {
  const { countrySlug } = useParams<{ countrySlug: string }>();
  const { t } = useLanguage();
  
  const country = worldCountries.find(c => c.slug === countrySlug);
  
  if (!country) {
    return (
      <>
        <SEO
          title="Country Not Found | Global Therapy Hub"
          description="The country you're looking for could not be found. Browse our therapist directory to find mental health professionals in your area."
          canonicalUrl="https://psychotherapist.help/directory/"
        />
        <div className="container mx-auto px-4 py-16 text-center">
          <h1 className="text-2xl font-bold text-brand-gray-900 mb-4">
            {t('countryNotFound')}
          </h1>
          <Button asChild>
            <Link to="/directory">
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t('backToDirectory')}
            </Link>
          </Button>
        </div>
      </>
    );
  }
  
  // Enhanced SEO title and description with target keyword
  const seoTitle = `Best Psychotherapists in ${country.name} | Licensed Mental Health Professionals`;
  const seoDescription = `Find the best psychotherapists in ${country.name}. Connect with certified mental health professionals offering therapy, counseling, and psychological services. Browse qualified psychotherapists in ${country.name} by city and specialty.`;
  const seoKeywords = `best psychotherapists in ${country.name}, psychotherapists ${country.name}, therapy ${country.name}, mental health ${country.name}, counseling ${country.name}, psychology ${country.name}, licensed therapists ${country.name}`;
  
  // Structured data for BreadcrumbList
  const breadcrumbStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://psychotherapist.help'
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Directory',
        item: 'https://psychotherapist.help/directory'
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: `Best Psychotherapists in ${country.name}`,
        item: `https://psychotherapist.help/country/${country.slug}`
      }
    ]
  };
  
  // Structured data for the city list
  const cityListStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: country.topCities.map((city, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'City',
        name: city.name,
        url: `https://psychotherapist.help/city/${city.slug}`
      }
    }))
  };
  
  return (
    <>
      <SEO
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        canonicalUrl={`https://psychotherapist.help/country/${country.slug}`}
        structuredData={[breadcrumbStructuredData, cityListStructuredData]}
        ogType="website"
      />
      
      <CountryHeroSection country={country} />
      
      <CountrySEOContent country={country} />
      
      <CountryCitiesGrid country={country} />
      
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <CountryRelatedSection country={country} />
          <CountryCallToAction country={country} />
        </div>
      </div>
    </>
  );
};

export default CountryDirectory;
